import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { Eye, EyeOff } from "lucide-react";
import { registerRequest } from "../../redux/auth/auth.actions";

// Styled Components (mirroring Login design)
const RegisterContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  margin: 0;
  background: white;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 55%;
  background: linear-gradient(135deg, #5b6ee5 0%, #6f4ea8 50%, #4b2f7f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -2px 12px rgba(0,0,0,0.25);

  @media (max-width: 1024px) {
    flex-basis: 50%;
  }
  @media (max-width: 900px) {
    flex-basis: 45%;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const IllustrationPlaceholder = styled.div`
  width: 80%;
  height: 80%;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 18px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.3);

  @media (max-width: 1280px) {
    width: 85%;
    height: 80%;
  }
`;

const RightSection = styled.div`
  flex: 0 0 45%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  @media (max-width: 1024px) {
    flex-basis: 50%;
  }
  @media (max-width: 900px) {
    flex-basis: 55%;
    padding: 1.5rem;
  }
  @media (max-width: 768px) {
    flex-basis: auto;
    width: 100%;
    padding: 1.25rem;
  }
`;

const RegisterCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    max-width: 420px;
  }
  @media (max-width: 768px) {
    max-width: 520px;
    padding: 1.5rem;
  }
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 1rem;
  }
`;

const RegisterTitle = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  line-height: 1.1;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }
`;

const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #6b7280;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: #fafafa2e;
  color: #1f2937;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(180deg, rgba(249, 250, 251, 0.95), rgba(243, 244, 246, 0.9));
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6);

  &:hover {
    color: #374151;
    background: #ffffff;
    transform: translateY(-50%) translateY(-1px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.12);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(139,92,246,0.18);
    color: #8b5cf6;
  }
`;

const RegisterButton = styled.button`
  width: 100%;
  background: linear-gradient(180deg, #8b5cf6, #7c3aed);
  color: white;
  border: none;
  padding: 0.875rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  box-shadow: 0 10px 20px rgba(139, 92, 246, 0.32), inset 0 1px 0 rgba(255,255,255,0.35);

  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #7c3aed, #6d28d9);
    transform: translateY(-1px);
    box-shadow: 0 14px 26px rgba(109, 40, 217, 0.36);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const RegisterFooter = styled.div`
  margin-top: 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const LoginLink = styled(Link)`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

function Register() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector(state => state.auth);
  
  const [registerData, setRegisterData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    dispatch(registerRequest({ 
      formValues: registerData,
      func: () => navigate("/login")
    }));
  };

  return (
    <RegisterContainer>
      <LeftSection>
        <IllustrationPlaceholder>
          Illustration Placeholder
        </IllustrationPlaceholder>
      </LeftSection>

      <RightSection>
        <RegisterCard>
          <RegisterTitle>
            Create your
            
            Account
          </RegisterTitle>

          <RegisterForm onSubmit={handleRegisterSubmit}>
            <InputGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                type="text"
                id="username"
                name="username"
                value={registerData.username}
                onChange={handleRegisterChange}
                required
                placeholder="Ash Ketchum"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={registerData.email}
                onChange={handleRegisterChange}
                placeholder="name@email.com"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  required
                  placeholder="********"
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>
            </InputGroup>

            <RegisterButton type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create account"}
            </RegisterButton>
          </RegisterForm>

          <RegisterFooter>
            Already have an account? <LoginLink to="/login">Login</LoginLink>
          </RegisterFooter>
        </RegisterCard>
      </RightSection>
    </RegisterContainer>
  );
}

export default Register;
